import { useState } from "react";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { faCheckDouble } from "@fortawesome/pro-solid-svg-icons";

import { UISubscriptionStatus } from "common/types";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import CrossedPrice from "client/common/components/content/CrossedPrice";
import QuantityInput from "client/common/components/inputs/QuantityInput";
import Button from "client/common/components/buttons/Button";
import { useCartItemAddMutation } from "client/carts/hooks";
import { CartCode } from "client/carts/types";
import { urls } from "client/routes";
import {
  sendAddToCart,
  sendSelectItem,
  useSendPostHogAddToCart,
  useSendPostHogItemClick,
} from "client/analytics/utils";
import { ItemClickSource } from "client/analytics/types";

import { DetailSubscription, ListSubscription } from "../types";

import styles from "../styles/SubscriptionItem.module.less";

type SubscriptionItemProps = {
  subscription: DetailSubscription | ListSubscription;
};

const SubscriptionItem = (props: SubscriptionItemProps) => {
  const { subscription } = props;
  const sendPostHogAddToCart = useSendPostHogAddToCart();
  const sendPostHogItemClick = useSendPostHogItemClick();

  // We may want to add a toast on error here.
  const { mutate: addCartItem, isLoading: isAddingToCart } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (cartItemId, quantity, body) => {
      // TODO: price here is the order total, not per-unit — consider using
      // next_order_data.unit_price in a future iteration.
      // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_to_cart
      const price = subscription.next_order_data.subtotal - subscription.next_order_data.discount;
      sendAddToCart({
        item: subscription.item,
        quantity,
        price,
        discount: subscription.next_order_data.discount,
      });
      sendPostHogAddToCart({
        cart_id: typeof body.cart_id === "number" ? body.cart_id : null,
        cart_item_id: cartItemId,
        item_id: body.item,
        product_id: subscription.item.product.id,
        product_line_id: subscription.item.product.product_line.id,
        patient_id: body.patient_id ?? null,
        flavor_selection_id: body.flavor_selection ?? null,
        quantity,
        unit_price: price,
        discount: subscription.next_order_data.discount,
      });
    },
  });

  const [quantity, setQuantity] = useState(subscription.quantity);

  const itemUrl = urls.products.detail({
    params: { id: subscription.item.product.product_line.id },
    search: {
      item: subscription.item.id,
      product: subscription.item.product.id,
    },
  });

  const addToCart = () => {
    addCartItem({
      item: subscription.item.id,
      quantity,
      cart_id: CartCode.ACTIVE,
      flavor_selection: subscription.flavor_selection || undefined,
      patient_id: subscription.patient.id,
    });
  };
  const handleItemClick = () => {
    sendSelectItem(
      subscription.item,
      ItemClickSource.Subscription,
      subscription.next_order_data.unit_price,
    );
    sendPostHogItemClick(subscription.item, {
      source: ItemClickSource.Subscription,
      unit_price: subscription.next_order_data.unit_price,
    });
  };
  const isSubscriptionActive = subscription.visible_status === UISubscriptionStatus.Active;

  const renderPrescriptionChip = () => {
    if (!subscription.has_prescription) {
      return (
        <>
          <Icon icon={faExclamationTriangle} /> Prescription needs approval
        </>
      );
    }

    if (subscription.prescription_needs_renewal_for_next_order) {
      return (
        <>
          <Icon icon={faExclamationTriangle} /> Prescription needs renewal
        </>
      );
    }

    return (
      <>
        <Icon icon={faCheckDouble} /> Active prescription on file
      </>
    );
  };

  return (
    <div className={styles.item}>
      <CatalogImage.Link
        dest={itemUrl}
        image={subscription.item.image}
        size="medium"
        alt={subscription.item.full_name}
        className={styles.image}
        onClick={handleItemClick}
      />
      <div className={styles.content}>
        <div>
          <LinkButton dest={itemUrl} className={styles.name} onClick={handleItemClick}>
            {subscription.item.product.product_line.name}
          </LinkButton>
          <p className={styles.description}>
            {subscription.item.product.name} · {subscription.item.name}
            {subscription.flavors_display && ` · ${subscription.flavors_display}`} · Qty{" "}
            {subscription.quantity}
          </p>
        </div>
        {subscription.item.product.product_line.prescription_required && (
          <TextChip>{renderPrescriptionChip()}</TextChip>
        )}
        <CrossedPrice
          crossed={subscription.next_order_data.subtotal}
          price={subscription.next_order_data.subtotal - subscription.next_order_data.discount}
        />
        <p className={styles.info}>{subscription.item.product.product_line.short_description}</p>
        {!isSubscriptionActive && (
          <div className={styles.reorder}>
            <QuantityInput value={quantity} onChange={setQuantity} min={1} />
            <Button className={styles.button} isLoading={isAddingToCart} onClick={addToCart}>
              Reorder Item
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionItem;
